import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import FreeItemsSection from "@/components/FreeItemsSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ArrowBigLeft, Truck, Zap, MessageSquare, CarFront, Hand, MapPinOff, Check, EyeOff } from "lucide-react";

const DEEP = "#1E40AF";
const SKY = "#B4C6F5";
const ROYAL = "#3B63E0";
const CATEGORY_PILLS = ["All", "Electronics", "Gaming", "Home", "Fashion", "Sports", "Kids", "Free Items", "More"];
const CATEGORY_OPTIONS = ["All", "Electronics", "Gaming", "Home", "Fashion", "Sports", "Kids"];
const CONDITIONS = ["Any", "New", "Like New", "Good", "Fair", "Poor"];
const PRICE_RANGES = [["any", "Any Price"], ["0-100", "Under AED 100"], ["0-500", "Under AED 500"], ["500-1000", "AED 500 – AED 1,000"]];

const TRUST = [
  { Icon: ShieldCheck, text: "Payment Held Until You Receive It" },
  { Icon: ArrowBigLeft, text: "24 Hours to Raise a Concern" },
  { Icon: Truck, text: "Delivered Across Dubai" },
  { Icon: Zap, text: "List in Under 30 Seconds" },
  { Icon: MessageSquare, text: "Max Negotiates for You" },
  { Icon: CarFront, text: "No Tyre Kickers" },
  { Icon: Hand, text: "No Haggling" },
  { Icon: MapPinOff, text: "No Meetups" },
  { Icon: Check, text: "Just Sell Stuff" },
  { Icon: EyeOff, text: "Contact Details Kept Private" },
];

function TrustItem({ Icon, text }) {
  return (
    <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-700">
      <Icon className="h-3.5 w-3.5" style={{ color: DEEP }} /> {text}
    </span>
  );
}

export default function Browse() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const search = params.get("search") || "";
  const category = params.get("category") || "All";
  const [condition, setCondition] = useState("Any");
  const [price, setPrice] = useState("any");
  const [sort, setSort] = useState("latest");

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All" || c === "More") next.delete("category"); else next.set("category", c);
    setParams(next);
  };

  const load = async () => {
    setLoading(true);
    try {
      const q = { sort };
      if (search) q.search = search;
      if (category !== "All" && category !== "More") q.category = category;
      if (condition !== "Any") q.condition = condition;
      if (price && price !== "any") { const [lo, hi] = price.split("-"); if (lo) q.price_min = lo; if (hi) q.price_max = hi; }
      const { data } = await api.get("/listings", { params: q });
      setListings(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, category, condition, price, sort]);

  const heading = useMemo(() => (search ? `Results for “${search}”` : "Just Listed"), [search]);

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes ss-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .ss-marquee-track { display: inline-flex; gap: 2rem; padding-left: 2rem; animation: ss-marquee 40s linear infinite; }
        .ss-marquee:hover .ss-marquee-track, .ss-marquee:active .ss-marquee-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .ss-marquee { overflow-x: auto; } .ss-marquee-track { animation: none; } }
      `}</style>

      {/* Tagline + trust strip */}
      <div className="border-y border-slate-200 bg-slate-50" data-testid="trust-strip">
        <div className="mx-auto flex h-9 max-w-7xl items-center px-4 sm:px-6">
          <span className="shrink-0 text-sm font-medium text-slate-800">Sell Smarter. Buy Safer.</span>
          <div className="ss-marquee ml-3 flex-1 overflow-hidden">
            <div className="ss-marquee-track">
              {[...TRUST, ...TRUST].map((t, i) => <TrustItem key={i} {...t} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Category row */}
      <div className="border-b border-slate-200 bg-white">
        <div className="ss-no-scrollbar mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3 sm:px-6" data-testid="category-row">
          {CATEGORY_PILLS.map((c) => {
            if (c === "Free Items") {
              return (
                  <a key={c} href="/free" data-testid="category-pill-Free-Items" className="flex h-9 shrink-0 items-center rounded-full px-4 text-sm font-medium transition-opacity hover:opacity-90" style={{ backgroundColor: "#B4C6F5", color: "#1E40AF" }}>
                  {c}
                </a>
              );
            }
            const active = (category === "All" && c === "All") || category === c;
            return (
              <button
                key={c}
                data-testid={`category-pill-${c}`}
                onClick={() => setCategory(c)}
                className="h-9 shrink-0 rounded-full px-4 text-sm font-medium transition-colors"
                style={active ? { backgroundColor: DEEP, color: "#fff" } : { backgroundColor: SKY, color: DEEP }}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="ss-no-scrollbar mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6" data-testid="filter-bar">
          <LabeledSelect testid="filter-category" label="Category" value={category} onChange={setCategory} options={CATEGORY_OPTIONS} />
          <LabeledSelect testid="filter-condition" label="Condition" value={condition} onChange={setCondition} options={CONDITIONS} />
          <LabeledSelect testid="filter-price" label="Price" value={price} onChange={setPrice} options={PRICE_RANGES} kv />
          <div className="ml-auto shrink-0">
            <LabeledSelect testid="filter-sort" value={sort} onChange={setSort}
              options={[["latest", "Latest"], ["price_low", "Price: Low to High"], ["price_high", "Price: High to Low"], ["oldest", "Oldest"]]} kv />
          </div>
        </div>
      </div>

      {/* Free Items section */}
      <FreeItemsSection />

      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 data-testid="just-listed-heading" className="font-heading text-lg font-bold text-slate-900">{heading}</h2>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        ) : listings.length === 0 ? (
          <div data-testid="empty-state" className="rounded-2xl border border-dashed border-slate-300 py-20 text-center text-slate-500">No listings match your filters.</div>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="listings-grid">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function LabeledSelect({ label, value, onChange, options, kv, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-auto shrink-0 gap-1 rounded-full border-slate-200 text-sm">
        {label && <span className="text-slate-500">{label}:</span>}<SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => kv ? <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem> : <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
